import { TFunction } from '@psyomics/ui-common';
import { useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Model } from 'survey-react-ui';

import {
  useDeleteReferralRequestSurveyAttachment,
  useGetReferralRequestSurveyAttachmentDownloadUrl,
} from '../hooks/use-survey-attachments';
import { useUpdateReferralRequestSurvey } from '../hooks/use-update-referral-request-survey';

import { createSurveyAttachmentHandlers } from './survey-attachment-handlers';
import { createSurveyEventHandlers } from './survey-event-handlers';
import { useDebouncedSurveyUpdate } from './use-debounced-survey-update';
import { useSurveyUpdateQueue } from './use-survey-update-queue';

type SurveyData = Record<string, unknown>;

type SurveyHandlers = ReturnType<typeof createSurveyEventHandlers>;
type SurveyAttachmentHandlers = ReturnType<typeof createSurveyAttachmentHandlers>;

function attachSurveyListeners(
  surveyModel: Model,
  handlers: SurveyHandlers,
  attachmentHandlers: SurveyAttachmentHandlers
): void {
  surveyModel.onCurrentPageChanged.add(handlers.onPageChange);
  surveyModel.onComplete.add(handlers.onComplete);
  surveyModel.onValueChanged.add(handlers.onValueChange);
  surveyModel.onUploadFiles.add(attachmentHandlers.onUploadFiles);
  surveyModel.onClearFiles.add(attachmentHandlers.onClearFiles);
  surveyModel.onDownloadFile.add(attachmentHandlers.onDownloadFile);
}

function detachSurveyListeners(surveyModel: Model, handlers: SurveyHandlers): void {
  surveyModel.onValueChanged.clear();
  surveyModel.onCurrentPageChanged.remove(handlers.onPageChange);
  surveyModel.onComplete.clear();
  surveyModel.onUploadFiles.clear();
  surveyModel.onClearFiles.clear();
  surveyModel.onDownloadFile.clear();
}

type UseAttachSurveyListenersProps = {
  deleteReferralRequestSurveyAttachment: ReturnType<typeof useDeleteReferralRequestSurveyAttachment>[1];
  getReferralRequestSurveyAttachmentDownloadUrl: ReturnType<typeof useGetReferralRequestSurveyAttachmentDownloadUrl>;
  referralApiUrl: string;
  surveyResponseId: string;
  t: TFunction;
  updateSurvey: ReturnType<typeof useDebouncedSurveyUpdate>;
};

function useAttachSurveyListeners(surveyModel: Model, props: UseAttachSurveyListenersProps): void {
  const {
    deleteReferralRequestSurveyAttachment,
    getReferralRequestSurveyAttachmentDownloadUrl,
    referralApiUrl,
    surveyResponseId,
    t,
    updateSurvey,
  } = props;

  useLayoutEffect(() => {
    const handlers = createSurveyEventHandlers({ updateSurvey });
    const attachmentHandlers = createSurveyAttachmentHandlers({
      deleteReferralRequestSurveyAttachment,
      getReferralRequestSurveyAttachmentDownloadUrl,
      referralApiUrl,
      referralRequestSurveyResponseId: surveyResponseId,
      t,
    });

    attachSurveyListeners(surveyModel, handlers, attachmentHandlers);
    return () => detachSurveyListeners(surveyModel, handlers);
  }, [
    deleteReferralRequestSurveyAttachment,
    getReferralRequestSurveyAttachmentDownloadUrl,
    referralApiUrl,
    surveyModel,
    surveyResponseId,
    t,
    updateSurvey,
  ]);
}

type UseSurveyHandlerProps = {
  onComplete: (data: SurveyData) => void;
  ready: boolean;
  referralApiUrl: string;
  reviewMode: boolean;
  surveyModel: Model;
  surveyResponseId: string;
  surveyResponseUpdatedAt?: Date;
  updateSurveyAnswers: ReturnType<typeof useUpdateReferralRequestSurvey>[1];
};

const useSurveyHandlers = ({
  onComplete,
  ready,
  referralApiUrl,
  reviewMode,
  surveyModel,
  surveyResponseId,
  surveyResponseUpdatedAt,
  updateSurveyAnswers,
}: UseSurveyHandlerProps) => {
  const { t } = useTranslation();
  const [, deleteReferralRequestSurveyAttachment] = useDeleteReferralRequestSurveyAttachment();
  const getReferralRequestSurveyAttachmentDownloadUrl = useGetReferralRequestSurveyAttachmentDownloadUrl();

  const enqueueUpdateSurvey = useSurveyUpdateQueue({
    onComplete,
    ready,
    reviewMode,
    surveyResponseId,
    surveyResponseUpdatedAt,
    updateSurveyAnswers,
  });
  const updateSurvey = useDebouncedSurveyUpdate(enqueueUpdateSurvey);

  useAttachSurveyListeners(surveyModel, {
    deleteReferralRequestSurveyAttachment,
    getReferralRequestSurveyAttachmentDownloadUrl,
    referralApiUrl,
    surveyResponseId,
    t,
    updateSurvey,
  });
};

export { useSurveyHandlers };
